import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { Box, Button, HStack, useTheme, VStack } from '@chakra-ui/react'
import { faBuilding } from '@fortawesome/free-regular-svg-icons'
import { Formik } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import IconButton from '../../../components/IconButton/IconButton'
import InputForm from '../../../components/InputForm/InputForm'
import UnderlineHeading from '../../../components/UnderlineText/UnderlineHeading'
import { useLoadingContext } from '../../../utils/hooks/Context/LoadingContext'
import InfoLabel from '../../EmployeeDetails/components/InfoLabel'
import { REPOSITORY } from './../../../api/repository/Repository'
import { useRequestStatusContext } from './../../../utils/hooks/Context/RequestStatusContext'
import ConfirmModal from './ConfirmModal'
const editingStyles = {
  input: {
    w: '100%',
  },
  stack: {
    w: '100%',
  },
}
const noEditStyles = {
  input: {
    minW: '20%',
    maxW: '100%',
  },
  stack: {
    minW: '20%',
    maxW: '100%',
  },
}

const styles = {
  edit: {
    ...editingStyles,
  },
  noEdit: { ...noEditStyles },
}
export default function CostCenterDetail() {
  const [isEditing, setIsEditing] = useState(false)
  const [openConfirm, setOpenConfirm] = useState(false)
  const {
    colors: { gray },
  } = useTheme()
  const navigate = useNavigate()
  const { id } = useParams()
  const [costCenterData, setCostCenterData] = useState({})
  const { handleOpen: handleOpenLoading, handleClose: handleCloseLoading } =
    useLoadingContext()

  const { handleOpen, handleSetTitle, handleChangeBody, handleClose } =
    useRequestStatusContext()

  const getCostCenterData = useCallback(async () => {
    handleOpenLoading()
    await REPOSITORY.wsEmpresa.company
      .costCenterDetail({ sCentroCosto: id })
      .then((res) => setCostCenterData(res.data))
      .catch((error) => {
        console.error(error)
        setCostCenterData({})
      })
    handleCloseLoading()
  }, [id])
  const onOpenConfirm = () => {
    setOpenConfirm(true)
  }
  const onCloseConfirm = () => {
    setOpenConfirm(false)
  }
  const onChangeEdit = () => {
    setIsEditing((prevState) => !prevState)
  }
  const onOkStatus = useCallback(
    (response, title = null) => {
      handleCloseLoading()
      handleSetTitle(title ? title : 'Colaborador actualizado', response.status)
      handleChangeBody(
        <Button variant="primary" onClick={handleClose}>
          {response.status === 200 ? 'Continuar' : 'Contactar a soporte ahora'}
        </Button>,
      )
      handleOpen()
    },
    [handleSetTitle, handleChangeBody, handleOpen, handleClose],
  )

  const onError = useCallback(
    (response, title = null) => {
      handleCloseLoading()
      handleSetTitle(
        title ? title : 'Error al editar colaborador',
        response.status,
      )
      handleChangeBody(
        <Button variant="primary" onClick={handleClose}>
          {response.status === 200 ? 'Continuar' : 'Contactar a soporte ahora'}
        </Button>,
      )
      handleOpen()
    },
    [handleSetTitle, handleChangeBody, handleOpen, handleClose],
  )
  const onBack = () => {
    navigate(-1)
  }

  const onDelete = async () => {
    onCloseConfirm()
    handleOpenLoading()
    await REPOSITORY.wsEmpresa.company
      .deleteCostCenter({ sCentroCosto: id })
      .then((res) => {
        handleCloseLoading()
        onOkStatus(res, res.data.sMensaje)
      })
      .catch((error) => {
        handleCloseLoading()
        onError(error.response, error.response.data.error)
      })
  }

  const Component = useMemo(() => {
    if (isEditing) return InputForm
    return InfoLabel
  })

  const finalStyles = useMemo(() => {
    if (isEditing) return styles.edit
    return styles.noEdit
  })

  useEffect(() => {
    getCostCenterData()
  }, [getCostCenterData])
  return (
    <>
      <ConfirmModal
        isOpen={openConfirm}
        text={
          'Está a punto de eliminar la sucursal seleccionada. ¿Desea continuar?'
        }
        onClose={onCloseConfirm}
        onConfirm={onDelete}
      />
      <Formik
        initialValues={costCenterData}
        enableReinitialize
        onSubmit={(values) => {
          handleOpenLoading()
          REPOSITORY.wsEmpresa.company
            .updateCostCenter({
              sCentroCosto: values.sCentroCosto,
              sDescripcion: values.sDescripcion,
            })
            .then((res) => {
              onOkStatus(res, res.data.sMensaje)
            })
            .catch((error) => {
              onError(error.response, error.response.data.error)
            })
        }}
      >
        {({ values, handleSubmit, handleChange }) => (
          <VStack
            w="100%"
            h="75vh"
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <VStack spacing="32px" w="100%" alignItems="flex-start">
              <UnderlineHeading
                text="Datos de Sucursal"
                variant="h5"
                color={gray['800']}
              />
              <HStack
                alignItems="flex-start"
                minW="20%"
                w="100%"
                spacing="16px"
              >
                <VStack {...finalStyles.stack} spacing="16px">
                  <Component
                    id="sDescripcion"
                    name="sDescripcion"
                    stackProps={{ ...finalStyles.input }}
                    title={'Nombre centro de costo'}
                    onChange={handleChange}
                    value={values?.sDescripcion}
                    w="100%"
                  />
                </VStack>
              </HStack>
              <UnderlineHeading
                text={'Acciones'}
                variant="h5"
                color={gray['800']}
              />
              <Box w="339px" h="56px" borderRadius={'6px'}>
                <IconButton
                  borderRadius={'6px'}
                  text={'ELIMINAR CENTRO DE COSTO'}
                  icon={faBuilding}
                  onClick={onOpenConfirm}
                  variant="error"
                  color="white"
                />
              </Box>
            </VStack>
            <HStack w="100%" justifyContent="center">
              <Box>
                <Button
                  variant="white"
                  onClick={isEditing ? onChangeEdit : onBack}
                >
                  Cancelar
                </Button>
              </Box>
              <Box>
                <Button onClick={!isEditing ? onChangeEdit : handleSubmit}>
                  {!isEditing ? 'Editar' : 'Guardar'}
                </Button>
              </Box>
            </HStack>
          </VStack>
        )}
      </Formik>
    </>
  )
}
